import { ClusterProduct } from '~/graphql/generated/graphql';
import { useClusterRegistryStore } from '~/stores/clusterRegistry';
import { useConfigStore } from '~/stores/config';
import { buildRegionSwitchUrl, omitNonTransferableQueryParams } from '~/utils/clusters';

const PORTAL_PREFIX = '/educators';
const LEGACY_PORTAL_PREFIX = '/schools';

const matchesPrefix = (path: string, prefix: string): boolean =>
  path === prefix || path.startsWith(`${prefix}/`);

/**
 * Cluster-product guard for the teacher portal. The portal's canonical home is
 * `/educators` on the schools clusters; consumer clusters must not serve it
 * once the schools hosts are live (`schools-clusters-enabled`):
 *
 *  - schools cluster → serve `/educators/**` natively; legacy `/schools/**`
 *    matches no page and falls through to the 404 (the path must not exist here).
 *  - consumer cluster, flag off → schools hosts are still dark, so the portal
 *    keeps serving on this cluster at `/educators/**`; legacy `/schools/**`
 *    canonicalises to `/educators/**` same-host so old links keep working.
 *  - consumer cluster, flag on → both prefixes cross to the region's schools
 *    cluster (its school data cluster), path carried over, secrets/PII
 *    query params stripped as on a region switch.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  const isPortalPath = matchesPrefix(to.path, PORTAL_PREFIX);
  const isLegacyPath = matchesPrefix(to.path, LEGACY_PORTAL_PREFIX);
  if (!isPortalPath && !isLegacyPath) {
    return;
  }

  const configStore = useConfigStore();
  if (configStore.isSchoolsCluster) {
    return;
  }

  const portalPath = isLegacyPath
    ? to.path.replace(LEGACY_PORTAL_PREFIX, PORTAL_PREFIX)
    : to.path;

  // navigateTo runs after an await here, so the Nuxt context (needed by
  // clusterRedirectPath / useRuntimeConfig) must be re-bound — see
  // 00.auth-route-protection.
  const nuxtApp = useNuxtApp();
  const schoolsClustersEnabled = await useFeatureFlags().getFlag('schools-clusters-enabled');

  if (!schoolsClustersEnabled) {
    if (isLegacyPath) {
      return await nuxtApp.runWithContext(() => navigateTo(
        { path: clusterRedirectPath(portalPath), query: to.query },
        { replace: true, redirectCode: 301 },
      ));
    }
    return;
  }

  const sibling = useClusterRegistryStore().clusters.find(cluster =>
    cluster.id === configStore.schoolDataClusterId && cluster.product === ClusterProduct.Schools,
  );
  // No schools sibling seeded for this region is a misconfig — keep serving
  // locally rather than dead-ending the teacher.
  if (!sibling) {
    nuxtApp.$logWarnMessage('No schools sibling cluster found for region; serving portal locally', {
      clusterCode: configStore.cluster?.code,
    });
    return;
  }

  return await nuxtApp.runWithContext(() => {
    const scheme = useRuntimeConfig().public.useHttp ? 'http' : 'https';
    const url = buildRegionSwitchUrl(sibling.host, sibling.pathPrefix, {
      scheme,
      path: portalPath,
      query: omitNonTransferableQueryParams(to.query),
    });
    return navigateTo(url, { external: true, replace: true, redirectCode: 301 });
  });
});
